import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import {
	type DocumentEditRequestDTO,
	type DocumentListResponseDTO,
	patchDocumentIdV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type Props = {
	requestContract: DocumentEditRequestDTO;
	id: number;
	activeTag?: string;
};

export const useDocumentEditMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse(({ requestContract, id }: Props) =>
			patchDocumentIdV2(requestContract, id),
		),
		onMutate: async ({ requestContract, id }: Props) => {
			await queryClient.cancelQueries({ queryKey: DocumentKeys.list() });
			const previous = queryClient.getQueryData<DocumentListResponseDTO>(
				DocumentKeys.list(),
			);
			if (previous) {
				const updated = {
					...previous,
					documents: previous.documents.map((doc) =>
						doc.id === id
							? { ...doc, title: requestContract.title ?? doc.title }
							: doc,
					),
				};
				queryClient.setQueryData(DocumentKeys.list(), updated);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(DocumentKeys.list(), context.previous);
			}
		},
		onSettled: (_, __, { activeTag }) => {
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.list(activeTag),
			});
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.allLists,
			});
		},
	});
};
